import CytoscapeComponent from "react-cytoscapejs";
import cytoscape, { Collection, Singular } from "cytoscape";

import elk from "cytoscape-elk";
import React from "react";

const GRAPH_TIMEOUT = 10000;

import {
  GraphData,
  GraphFragment,
  NodeType,
  QualifiedFeatureFragment,
  QualifiedFeatureQualifiers,
  RuleType,
} from "fe-common/graphql";
import { featuresToMultiLine } from "fe-common/logic";
import {
  CustomScratchData,
  DataElement,
  filterUnreachableElements,
  GraphSet,
  reduceVisibleNodes,
  shortestPath,
} from "./graphalgo";
import { GraphColors } from "./styles";
import { timeoutPromise } from "fe-common/async/timeout";
import { cyStateManager } from "./cytoscapeStateManager";
import { CONDENSED_FONT } from "../styles/typography";

try {
  //cytoscape.use(klay);
  cytoscape.use(elk);
} catch (e) {
  console.log(e);
}

type GQualifiedFeature = QualifiedFeatureFragment;
type GQualifier = QualifiedFeatureQualifiers;

//#region Graph Data

// Visibility Flags - Determines which nodes are shown
const DATA_SHOWTYPE = "showTypes";
const DATA_SHOW_LOW_WEIGHT = "lowWeight";

//#endregion

//#region Styling
export const DISEASE_COLOR = "#d9482e";
export const FINDING_COLOR = "#3d7a52";

function lowZoomScale(ele) {
  let zoom = ele.cy().zoom();
  return zoom >= 0.6 ? 1 : (1 / zoom) * 0.6;
}

function labelSizeW(ele) {
  let size = 160 * lowZoomScale(ele);
  return "12em"; //`${size}px`;
}

function labelSizeH(ele) {
  let size = 70 * lowZoomScale(ele);
  return "4em"; //`${size}px`;
}
function circleSize(ele) {
  let size = 140 * lowZoomScale(ele);
  return "9.5em"; //`${size}px`;
}

function fontSize(ele) {
  let size = 18 * lowZoomScale(ele);
  return "1.2em"; // `${size}px`;
}
function label(ele) {
  return ele.data(DataElement.label);
}

function elemOpacity(ele) {
  if (ele.isNode()) return 1;
  if (ele.data("isa") == true) {
    let parent = ele.source();

    return parent ? opacityByWeight(parent) : 1;
  } else return opacityByWeight(ele);
}

function opacityByWeight(ele) {
  if (ele.selected()) return 1;
  let parent = ele.source().data(DataElement.weight) || 1;
  let self = ele.data(DataElement.weight) || 1;
  if (ele.data("ASSOCIATION") == true) self = self * 0.7;
  let w = Math.min(self, parent) || 1;
  return w * 0.2 + 0.8;
}

function blacknessByWeight(ele) {
  return (1 - opacityByWeight(ele)) * -1;
}

function zIndex(ele) {
  return Math.round(opacityByWeight(ele) * 100);
}
var baseStyleSheet = ({ reverseColoring }: { reverseColoring: boolean }) => [
  {
    selector: "node",
    style: {
      // display: displayFunc,
      label: label,
      width: labelSizeW,
      height: labelSizeH,
      shape: "round-rectangle",
      "border-width": 1,
      "border-color": GraphColors.nodeBackground,
      "font-size": fontSize,
      "text-wrap": "wrap",
      "text-max-width": labelSizeW,
      "text-halign": "center",
      "text-valign": "center",
      "background-blacken": blacknessByWeight,
      padding: "1px",
      "background-color": GraphColors.nodeBackground,
      color: GraphColors.nodeText,
      "font-family": CONDENSED_FONT,
      "z-index": zIndex,
    },
  },
  {
    selector: FINDING_SELECTOR,
    style: {
      "background-color": GraphColors.findingBackground,
      "pie-size": "100%",
      // "pie-1-background-color": "blue",
      // "pie-1-background-size": 10,
      // "pie-1-background-opacity": "0.7",
    },
  },
  {
    selector: FINDING_SUBTYPE_SELECTOR,
    style: {
      "background-color": GraphColors.findingSubtypeBackground,
      "pie-size": "100%",
      // "pie-1-background-color": "blue",
      // "pie-1-background-size": 10,
      // "pie-1-background-opacity": "0.7",
    },
  },
  {
    selector: DISEASE_SELECTOR,
    style: {
      "border-color": GraphColors.diseaseBorder,
      width: circleSize,
      height: circleSize,
      "border-width": 4,
      "text-wrap": "wrap",
      "text-max-width": "9em",
      shape: "ellipse",
      color: GraphColors.diseaseText,
      "background-color": "white",
    },
  },
  {
    selector: "edge",
    style: {
      //"curve-style": "unbundled-bezier",
      "curve-style": "bezier",
      // 'target-arrow-shape': 'triangle',
      width: 5,
      "line-color": GraphColors.edgeBackground,
      "target-arrow-color": GraphColors.edgeBackground,
      "source-arrow-color": GraphColors.edgeBackground,
      // display: displayFunc,
      opacity: opacityByWeight,
      "text-rotation": "autorotate",
      "line-style": "dashed",
      color: "white",
      "font-size": "8px",
      padding: 5,
      "target-arrow-scale": 2,
      "source-arrow-scale": 2,
    },
  },

  {
    selector: "edge[ISA]",
    style: {
      "target-arrow-shape": "none",
      "source-arrow-shape": "none",
      "line-color": GraphColors.edgeBackgroundLight,
      opacity: function(ele) {
        let parent = ele.source();
        return parent ? opacityByWeight(parent) : 1;
      },
    },
  },
  {
    selector: "edge[?shortpath]",
    style: {
      "line-color": GraphColors.diseaseBorder,
      "target-arrow-color": GraphColors.diseaseBorder,
      "source-arrow-color": GraphColors.diseaseBorder,
    },
  },

  {
    selector: "edge[SUBTYPE]",
    style: {
      "target-arrow-shape": "vee",
      "source-arrow-shape": "none",
    },
  },

  {
    selector: "edge[ASSOCIATION]",
    style: {
      "target-arrow-shape": "triangle",
      "source-arrow-shape": "triangle",
    },
  },
  {
    selector: "edge[LIFT]",
    style: {
      "target-arrow-shape": "triangle",
      "source-arrow-shape": "triangle",
    },
  },
  {
    selector: "edge[SOURCE_CAUSE_TARGET]",
    style: {
      "target-arrow-shape": "triangle",
      "source-arrow-shape": "none",
    },
  },
  {
    selector: "edge[TARGET_CAUSE_SOURCE]",
    style: {
      "target-arrow-shape": "triangle",
      "source-arrow-shape": "none",
    },
  },
  {
    selector: "edge[?emphasis]",
    style: {
      "line-color": "green",
    },
  },

  {
    selector: ":selected",
    style: {
      // "background-color": "#a5a6a4",
      "line-color": GraphColors.selectionDark,
      "target-arrow-color": GraphColors.selectionDark,
      "source-arrow-color": GraphColors.selectionDark,
      "border-width": 3,
      "border-color": GraphColors.selectionDark,
    },
  },
  {
    selector: ".hidden",
    style: {
      opacity: 0,
      display: "none",
      "transition-duration": "900ms",
      "transition-property": "opacity display",
      "transition-timing-function": "ease-in-sine",
    },
  },
  {
    selector: ".shown",
    style: {
      opacity: elemOpacity,
      "transition-duration": "900ms",
      "transition-property": "opacity display",
      display: "element",
      "transition-timing-function": "ease-in-sine",
    },
  },
  {
    selector: ".dimmed",
    style: {
      opacity: 0.3,
    },
  },
];

//#endregion

//#region Layout Options

var elkStyle = {
  name: "elk",
  nodeDimensionsIncludeLabels: true, // Boolean which changes whether label dimensions are included when calculating node dimensions
  fit: true, // Whether to fit
  // padding: 30, // Padding on fit
  animate: true, // Whether to transition the node positions
  animationDuration: 700, // Duration of animation in ms if enabled
  animationEasing: "ease", // Easing of animation if enabled
  transform: function(node, pos) {
    return pos;
  }, // A function that applies a transform to the final node position
  ready: undefined, // Callback on layoutready
  stop: undefined, // Callback on layoutstop
  ranker: "longest-path",

  elk: {
    algorithm: "layered",
    "org.eclipse.elk.direction": "DOWN",
    "org.eclipse.elk.layered.spacing.edgeNodeBetweenLayers": 5,
    "org.eclipse.elk.layered.spacing.edgeNode": 5,
    "org.eclipse.elk.edge.thickness": 10,
    "org.eclipse.elk.spacing.edgeEdge": 30,
    "org.eclipse.elk.spacing.nodeNode": 20,
    "org.eclipse.elk.layered.spacing.nodeNodeBetweenLayers": 15,
    "org.eclipse.elk.layered.nodePlacement.strategy": "BRANDES_KOEPF", //"LINEAR_SEGMENTS",
    "org.eclipse.elk.edgeRouting": "ORTHOGONAL",
    "org.eclipse.elk.aspectRatio": 3,
    "org.eclipse.elk.padding": 10,
  },
  roots: null,
};

var klayStyle = {
  name: "klay",
  nodeDimensionsIncludeLabels: true, // Boolean which changes whether label dimensions are included when calculating node dimensions
  fit: false, // Whether to fit
  // padding: 30, // Padding on fit
  animate: true, // Whether to transition the node positions
  animationDuration: 700, // Duration of animation in ms if enabled
  animationEasing: "ease", // Easing of animation if enabled
  transform: function(node, pos) {
    return pos;
  }, // A function that applies a transform to the final node position
  ready: undefined, // Callback on layoutready
  stop: undefined, // Callback on layoutstop
  ranker: "longest-path",

  klay: {
    // Following descriptions taken from http://layout.rtsys.informatik.uni-kiel.de:9444/Providedlayout.html?algorithm=de.cau.cs.kieler.klay.layered
    addUnnecessaryBendpoints: false, // Adds bend points even if an edge does not change direction.
    aspectRatio: 1.6, // The aimed aspect ratio of the drawing, that is the quotient of width by height
    // borderSpacing: 10, // Minimal amount of space to be left to the border
    compactComponents: false, // Tries to further compact components (disconnected sub-graphs).
    crossingMinimization: "LAYER_SWEEP", // Strategy for crossing minimization.
    cycleBreaking: "GREEDY", // Strategy for cycle breaking. Cycle breaking looks for cycles in the graph and determines which edges to reverse to break the cycles. Reversed edges will end up pointing to the opposite direction of regular edges (that is, reversed edges will point left if edges usually point right).
    direction: "DOWN", // Overall direction of edges: horizontal (right / left) or vertical (down / up)
    edgeRouting: "SPLINES", // Defines how edges are routed (POLYLINE, ORTHOGONAL, SPLINES)
    edgeSpacingFactor: 2, // Factor by which the object spacing is multiplied to arrive at the minimal spacing between edges.
    feedbackEdges: false, // Whether feedback edges should be highlighted by routing around the nodes.
    //fixedAlignment: "NONE", // Tells the BK node placer to use a certain alignment instead of taking the optimal result.  This option should usually be left alone.
    inLayerSpacingFactor: 2, // Factor by which the usual spacing is multiplied to determine the in-layer spacing between objects.
    layoutHierarchy: false, // Whether the selected layouter should consider the full hierarchy
    linearSegmentsDeflectionDampening: 0.0, // Dampens the movement of nodes to keep the diagram from getting too large.
    mergeEdges: false, // Edges that have no ports are merged so they touch the connected nodes at the same points.
    mergeHierarchyCrossingEdges: false, // If hierarchical layout is active, hierarchy-crossing edges use as few hierarchical ports as possible.
    nodeLayering: "NETWORK_SIMPLEX", // Strategy for node layering.
    nodePlacement: "LINEAR_SEGMENTS", //"BRANDES_KOEPF", // Strategy for Node Placement
    randomizationSeed: 1, // Seed used for pseudo-random number generators to control the layout algorithm; 0 means a new seed is generated
    routeSelfLoopInside: false, // Whether a self-loop is routed around or inside its node.
    separateConnectedComponents: false, // Whether each connected component should be processed separately
    spacing: 10, // Overall setting for the minimal amount of space to be left between objects
    thoroughness: 12, // How much effort should be spent to produce a nice layout..
  },
  priority: function(edge) {
    return null;
  }, // Edges with a non-nil value are skipped when geedy edge cycle breaking is enabled
  roots: null,
};
//#endregion

const DISEASE_SELECTOR = 'node[type = "DISEASE"]';
const FINDING_SELECTOR = 'node[type = "FINDING"]';
const FINDING_SUBTYPE_SELECTOR = 'node[type = "FINDING_SUBTYPE"]';

const LOWWEIGHT_CUTOFF = 15;
var UNIQ = 1;

export class PathGraph extends React.PureComponent<{
  elements: any;
  focused?: string;
  onEvent?: (e: any) => void;
  reverseColoring?: boolean;
  bgcolor?: string;
  showIsA?: boolean;
  defaultSelectedNodeId: string;
}> {
  cy: cytoscape.Core = null;
  root: cytoscape.NodeCollection;
  leaf: cytoscape.NodeCollection;
  extraOptions = {};
  layout = elkStyle;
  visibleFlags = {
    [DATA_SHOWTYPE]: this.props.showIsA ? GraphSet.exclude : GraphSet.include,
    [DATA_SHOW_LOW_WEIGHT]: GraphSet.exclude,
  };
  focusedNodeId = null;
  truncatedNodes = 0;

  constructor(props) {
    super(props);
    // try putting this here
    // console.log("cyto", cytoscapeUse != null, "klay", klay != null);
    // cytoscapeUse(klay);
  }

  //#region Visibility Show/Hide
  /**
   * True if this element should be shown based on current visibility-flags
   * @param ele
   */
  flagFilter = (ele) => {
    let hasOffFlag = Object.keys(this.visibleFlags).some(
      (k) => ele.data(k) && ele.data(k) != this.visibleFlags[k],
    );
    return !hasOffFlag;
  };

  display = (ele) => {
    let flagFilter = this.flagFilter(ele);
    return ele.data(DataElement.shown) && flagFilter ? "element" : "none";
  };

  /**
   * top level test of visibility of node or edge, takes into account any
   * reason element might be hidden
   * @param ele
   */
  isVisible = (ele: Singular<any, any>) => {
    if (ele.isNode()) {
      return this.display(ele) == "element";
    } else if (ele.isEdge()) {
      return (
        this.display(ele) &&
        this.display(ele.source()) == "element" &&
        this.display(ele.target()) == "element"
      );
    }
  };
  //#endregion

  //#region Exported Public Callbacks
  getTruncatedNodeCount(): number {
    return this.truncatedNodes;
  }
  postMessage = (s) => {
    if (this.props.onEvent) this.props.onEvent(s);
  };
  resize = () => {
    if (this.cy) {
      this.cy.resize();
      //this.cy.fit();
      this.cy.zoom(1.0);
    }
  };
  focusNodeByFinding(id: string) {
    this.focusedNodeId = id;
    if (this.cy) {
      //let ele = this.cy.nodes().rulesFilter();
      const ele = this.getNodeByFinding(this.cy, id);

      console.log(
        "found ele",
        ele,
        this.cy.nodes().map((n) => n.data("findingId")),
      );
      if (ele.nonempty()) this.focusNode(ele.nodes().first());
    }
  }
  getNodeByFinding(cy: cytoscape.Core, id: string) {
    let ele = cy.elements(`node[findingId = "${id}"]`);
    if (ele.empty()) {
      ele = cy.elements(`node[findingid = "${id}"]`);
    }
    if (ele.empty()) {
      ele = cy.elements(`node[sourceQF = "${id}"]`);
    }
    return ele;
  }
  addLayoutOption(options: any) {
    this.extraOptions = options || {};
    this.runLayout();
  }
  showUsedInComp(f) {
    if (this.cy) {
      this.cy.elements("edge[?usedInComputation]").data("emphasis", f);
    }
  }
  toggleLowWeight(f) {
    if (this.cy) {
      this.visibleFlags[DATA_SHOW_LOW_WEIGHT] = f
        ? GraphSet.include
        : GraphSet.exclude;
      filterUnreachableElements(this.cy, this.isVisible);
      this.syncElementsVisibility();
      // this.refocusNode();
      this.runLayout();
    }
  }
  collapseIsA(f) {
    if (this.cy) {
      this.visibleFlags[DATA_SHOWTYPE] = f
        ? GraphSet.include
        : GraphSet.exclude;
      this.syncElementsVisibility();
      // this.refocusNode();
      this.runLayout();
    }
  }

  //#endregion

  runLayout(collection?: any, notify = true) {
    collection = collection || this.cy.filter(".shown");
    let layoutname = this.layout.name;
    let defer = new Deferred();
    let promise: Promise<any> = defer.promise;

    if (this.cy) {
      this.cy.zoom(1.0);
      // move root to top center position
      let root = collection.filter("node[root]");
      let l = collection.layout({
        ...this.layout,
        [layoutname]: { ...this.layout[layoutname], ...this.extraOptions },
        ready: defer.resolve,
      });
      promise = defer.promise
        .then(() => {
          if (notify) this.postMessage({ op: "layout" });
          if (root) {
            let bb = root.boundingBox({});
            let ext = this.cy.extent();
            root.position({
              x: (ext.x2 - ext.x1) / 2,
              y: (bb.y2 - bb.y1) / 2,
            });
          }
        })
        .catch(console.error);

      try {
        l.run();
      } catch (e) {
        console.log(e);
      }
    }
    return promise;
  }

  syncElementsVisibility(elements?: cytoscape.Collection) {
    if (!elements) elements = this.cy.elements();
    elements.forEach((e) => {
      e.style("display", this.display(e));
      if (this.display(e) == "element") {
        e.classes("shown");
      } else {
        e.classes("hidden");
      }
    });
  }

  currentFocusNode: string = null;

  setFocusNode = (id: string) => {
    this.currentFocusNode = id;
    this.postMessage({ op: "focus", value: id });
  };

  focusNode = async (n: cytoscape.NodeSingular) => {
    if (n.isEdge()) {
      return;
    }
    let { cy } = this;
    let all = this.getAllConnected(n);

    this.setFocusNode(n.id());
    await Promise.all([
      this.fadeInOut(all, true),
      this.fadeInOut(cy.elements().not(all), false),
    ]);
    await this.runLayout(all);
  };

  getAllConnected = (n: cytoscape.NodeSingular) =>
    n
      .union(successorsWithSelect(n, this.isVisible))
      .union(predeccessorsWithSelect(n, this.isVisible));

  resetFocus = async () => {
    if (this.currentFocusNode) {
      this.setFocusNode(null);
      let all = this.cy.elements().filter(this.isVisible);
      await this.fadeInOut(all, true);
      await this.runLayout(all);
    }
  };

  clearFocusImmediate = () => {
    this.setFocusNode(null);
    let all = this.cy.elements().filter(this.isVisible);
    all.classes("shown");
  };

  isFocused = () => {
    return this.currentFocusNode != null;
  };

  removeWithFade = (elems) => {
    let defer = new Deferred();
    if (!elems.nonempty()) defer.resolve();
    elems
      .animate({
        style: { opacity: 0.1 },
        duration: 500,
        easing: "ease-in-sine",
      })
      .delay(200, () => {
        this.cy.remove(elems);
        defer.resolve();
      });
    return defer.promise;
  };

  fadeInOut = (elems, show) => {
    let defer = new Deferred();
    if (elems.empty()) defer.resolve();
    if (show) {
      let restoreNodes = elems.filter(".hidden");
      if (restoreNodes) {
        restoreNodes.classes("shown");
      }
      defer.resolve();
    } else {
      let restoreNodes = elems.filter(".shown");
      if (restoreNodes && restoreNodes.nonempty()) {
        restoreNodes
          .animate({
            style: { opacity: 0 },
            duration: 900,
            easing: "ease-in-sine",
          })
          .delay(300, () => {
            restoreNodes.classes("hidden");
            defer.resolve();
          });
      } else defer.resolve();
    }
    return defer.promise;
  };

  __fadeInOut = (elems, show) => {
    let defer = new Deferred();
    if (elems.empty()) defer.resolve();
    if (show) {
      let restoreNodes = elems.filter(".hidden");
      if (restoreNodes && restoreNodes.nonempty()) {
        restoreNodes
          .animate({
            style: { opacity: 1.0 },
            duration: 500,
            easing: "ease-in-sine",
          })
          .delay(300, () => {
            elems.style("opacity", null);
            elems.removeClass("hidden");
            elems.addClass("shown");
            defer.resolve();
          });
      } else {
        elems.style("opacity", null);
        elems.removeClass("hidden");
        elems.addClass("shown");
        defer.resolve();
      }
    } else {
      elems
        .animate({
          style: { opacity: 0.0 },
          duration: 500,
          easing: "ease-in-sine",
        })
        .delay(300, () => {
          elems.removeClass("shown");
          elems.addClass("hidden");
          // cancel custom style
          elems.style("opacity", null);
          elems.style("display", null);
          defer.resolve();
        });
    }
    return defer.promise;
  };

  counter = 1;

  softReplaceData = async (
    elements: cytoscape.Collection,
    clearFocus: boolean = true,
  ) => {
    let r = this.counter++;
    // fiind all elements in common
    let updatedElements = [];
    let toRemove = this.cy.collection();

    this.cy.startBatch();
    this.cy.elements().forEach((e) => {
      let id = e.data("id");

      //let replace = elements.find((f) => f.data.id == id);
      let replace = elements.getElementById(id);

      if (replace && replace.nonempty()) {
        e.removeData();
        e.data(replace.data());
        e.scratch(
          CustomScratchData.subGraph,
          replace.scratch(CustomScratchData.subGraph),
        );
        e.classes(replace.classes() as any);
        updatedElements.push(e);
      } else {
        toRemove = toRemove.union(e);
      }
    });
    this.cy.endBatch();

    // now add new elements
    let newElems = elements.filter(
      (e) => !updatedElements.some((id) => id == e.id()),
    );
    await this.removeWithFade(toRemove);
    let newAdd = this.cy.add(newElems);
    let newVisibleGraph = [...updatedElements, ...newAdd.toArray()].filter(
      this.isVisible,
    );

    if (clearFocus) await this.clearFocusImmediate();

    let collection = arrayToCollection(this.cy.collection(), newVisibleGraph);
    this.runLayout(collection, false); // skip notification while adding elements since the notification is sent while graph is incomplete
  };

  initCySettings = (cy) => {
    // klay(cy);

    cy.maxZoom(1.0);
    cy.minZoom(0.3);
    // add callbacks
    cy.removeAllListeners();
    cy.on("zoom", function(e) {
      //      console.log("zoom", cy.zoom());
      (cy.style() as any).update();
    });
    cy.on("tap", "node", (e) => {
      this.focusNode(e.target);
    });
    cy.on("select", (e) => {
      let n = e.target;
      this.postMessage({
        op: "select",
        selected: n.selected(),
        type: n.isEdge() ? "edge" : "node",
        id: n.data("id"),
        obj: n,
      });
    });
    cy.on("unselect", (e) => {
      let n = e.target;
      this.postMessage({
        op: "unselect",
        selected: n.selected(),
        type: n.isEdge() ? "edge" : "node",
        id: n.data("id"),
        obj: n,
      });
    });
  };

  setGraph = async (elements: any) => {
    // put elements in temporary graph for processing
    const tempGraph = cytoscape({
      headless: true,
      elements,
    });
    tempGraph.elements().data(DataElement.shown, true);

    // set root and leaf
    let root: any = tempGraph.elements(DISEASE_SELECTOR);
    let leaf: any = tempGraph.nodes("[?isConnected]").leaves();
    root.data("root", true);
    leaf.data("leaf", true);

    // set shortest path
    tempGraph.elements(FINDING_SELECTOR).forEach((l) => {
      let path = shortestPath(tempGraph.elements(), root, l);
      if (path) (path as any).data(DataElement.shortpath, true);
    });
    // this.root = root;
    // this.leaf = leaf;

    // set initial view settings
    //markCollapsedIsA(tempGraph, DATA_SHOWTYPE);
    /*this.truncatedNodes = reduceVisibleNodes(
      tempGraph,
      LOWWEIGHT_CUTOFF,
      DATA_SHOW_LOW_WEIGHT,
      this.isVisible,
    );*/
    filterUnreachableElements(tempGraph, this.isVisible);
    this.syncElementsVisibility(tempGraph.elements());

    // port graph to live instance of cy
    if (this.props.defaultSelectedNodeId) {
      const n = this.getNodeByFinding(
        tempGraph,
        this.props.defaultSelectedNodeId,
      );
      const connectedElements = this.getAllConnected(n);
      const unConnectedElements = tempGraph.elements().not(connectedElements);
      unConnectedElements.classes("hidden");
    }

    const elementsToPort = tempGraph.elements().remove();

    await this.softReplaceData(
      elementsToPort,
      !this.props.defaultSelectedNodeId,
    );
    // kill tempGraph
    tempGraph.destroy();
  };

  setupCy = async () => {
    let cy = this.cy;
    //  cy.resize();

    //Since cytoscape destroyes that in unmount, we might get exceptions when we are during layout
    //This overrides the bug
    this.cy.destroy = () => {};
    this.initCySettings(cy);

    // await this.setGraph(this.props.elements);
    // await this.runLayout();
  };

  componentDidUpdate(
    prevProps: Readonly<{
      elements: any;
      focused?: string;
      onEvent?: (e: any) => void;
      reverseColoring?: boolean;
    }>,
  ): void {
    if (prevProps.elements != this.props.elements) {
      (async () => {
        if (!cyStateManager.loading) {
          do {
            cyStateManager.loading = true;
            cyStateManager.elements = this.props.elements;
            await this.updateGraph();
            cyStateManager.loading = false;
          } while (cyStateManager.elements != this.props.elements);
        }
      })();
    }
  }

  updateGraph = async () => {
    return Promise.race([
      this.setGraph(this.props.elements),
      timeoutPromise(GRAPH_TIMEOUT),
    ]);
    //await this.runLayout();
  };

  render() {
    // temp code for debugging
    let cas = this.cy as any;
    if (cas && !cas.uniq) cas.uniq = UNIQ++;

    /*Due to cytoscape bug, try to avoid padding in the cytoscape style
     * It causes it to crash when it's size is still 0,0*/
    return (
      <div style={{ flex: 1, height: "100%", width: "100%" }}>
        <CytoscapeComponent
          pixelRatio={"1"}
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: this.props.bgcolor ? this.props.bgcolor : "white",
          }}
          className="flex-grow-3"
          elements={[]}
          layout={elkStyle}
          stylesheet={baseStyleSheet({
            reverseColoring: this.props.reverseColoring,
          })}
          cy={(cy) => {
            this.cy = cy;
            this.setupCy();
            //process.nextTick(() => this.setupCy());
          }}
        />
      </div>
    );
  }
}

function nameQF(qf: GQualifiedFeature) {
  let name: any = qf.feature.name;
  let qualifierName = (q: GQualifier) =>
    q.range && q.range.units
      ? `${q.range.low}-${q.range.high} ${q.range.units}`
      : q.value.name;
  let qual =
    qf.qualifiers && qf.qualifiers.length
      ? qf.qualifiers.map(qualifierName).join("|")
      : null;

  return `${name} ${qual ? "|" + qual : ""}`;
}

export function graphToCyto(root: GQualifiedFeature, graph: GraphData) {
  let nodesQF = graph.nodes.map((n) => ({
    ...n,
    feature: n.qfs[0],
  }));

  let name = nameQF;

  let nodes = nodesQF.map((n) => {
    return {
      ...n,
      id: n.id,
      uid: n.qfs.map((q) => q.id).concat("!"),
      name: featuresToMultiLine(n.qfs),
      label: featuresToMultiLine(n.qfs, "\n", true).substr(0, 60),
      weight: n.probability,
      root: root && n.feature.id == root.id ? true : undefined,
      features: n.qfs,
    };
  });

  let edges = graph.edges.map((e) => {
    let newedge: any = {
      ...e,
      id: e.id,
      uid: e.id,
      source: e.sourceID,
      target: e.targetID,
      weight: e.prob,
      edgeid: e.id,
      CITATION: e.ruleValues != null && e.ruleValues.length > 0,
    };
    if (e.type === RuleType.Isa) newedge.isa = true;
    if (e.type === RuleType.TargetCauseSource) {
      newedge.source = e.targetID;
      newedge.target = e.sourceID;
    }
    newedge[e.type] = true;
    return newedge;
  });

  // put parent relationships in
  // let removedEdges = [];
  // nodes
  //   .filter((n) => n.type == NodeType.FindingSubtype)
  //   .forEach((n) => {
  //     // find the parent node
  //     let edge = edges.find((e) => e.source == n.id && e.type == RuleType.Isa);
  //     if (edge) n["parent"] = edge.target;
  //     if (edge) removedEdges.push(edge);
  //   });
  // edges = edges.filter((e) => !removedEdges.includes(e));

  return {
    root: nodes.find((n) => n.root),
    nodes: nodes.map((v) => ({ data: v })),
    edges: edges.map((v) => ({
      data: v,
      selectable: true,
    })),
    rawEdges: graph.edges.reduce((map, e) => {
      map[e.id] = e;
      return map;
    }, {}),
    rawNodes: nodesQF.reduce((map, n) => {
      map[n.id] = n;
      return map;
    }, {}),
  };
}
export function cytoData({
  start,
  graphs,
}: {
  start: GQualifiedFeature;
  graphs: GraphData[];
}) {
  let trees = graphs.map((g) => graphToCyto(start, g));

  //merge
  let nodes = new Map();
  let edges = new Map();

  trees.forEach((t) => {
    t.nodes.forEach((n) => {
      if (nodes.get(n.data.id)) console.log("DUPLICATE NODE");
      nodes.set(n.data.id, n);
    });
    t.edges.forEach((e) => {
      if (edges.get(e.data.id)) console.log("DUPLICATE EDGE");
      edges.set(e.data.id, e);
    });
  });

  return {
    elements: [...nodes.values(), ...edges.values()],
  };
}

// fix traversal

const traverse = function(params) {
  return function(eles, filter) {
    let sEles = [];
    let sElesIds = {};
    let collection = null;
    for (;;) {
      let next = params.outgoing
        ? eles.outgoers().filter(filter)
        : eles.incomers().filter(filter);

      if (next.length === 0) {
        break;
      } // done if none left

      let newNext = false;
      for (let i = 0; i < next.length; i++) {
        let n = next[i];
        let nid = n.id();

        if (!sElesIds[nid]) {
          sElesIds[nid] = true;
          sEles.push(n);
          collection = collection ? collection.union(n) : n;
          newNext = true;
        }
      }

      if (!newNext) {
        break;
      } // done if touched all outgoers already

      eles = next;
    }

    return collection;
  };
};
const successorsWithSelect = traverse({ outgoing: true });
const predeccessorsWithSelect = traverse({ outgoing: false });

class Deferred {
  promise: Promise<void>;
  resolve: () => void;
  reject: () => void;
  constructor() {
    this.promise = new Promise((resolve, reject) => {
      this.resolve = resolve;
      this.reject = reject;
    });
  }
}

function arrayToCollection(c: Collection, elems: Singular[]) {
  let union = function(a, b) {
    return a.union(b);
  };
  return elems.reduce(union, c);
}
